import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Shield, ArrowLeft, FileText, AlertCircle, 
  ArrowRight, CheckCircle
} from 'lucide-react';
import axios from 'axios';
import AppFooter from '../components/AppFooter';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BUREAUS = [
  { id: 'equifax', label: 'Equifax' },
  { id: 'experian', label: 'Experian' },
  { id: 'transunion', label: 'TransUnion' }
];

// Smart factual reasons by issue type - prevents frivolous disputes
const REASONS_BY_TYPE = {
  "Late Payment": [
    { id: 'never_late', label: 'I was never late on this account — records will confirm on-time payments' },
    { id: 'incorrect_date_late', label: 'The reported late date is inaccurate — payment was made on time' },
    { id: 'not_mine_late', label: 'This account does not belong to me' },
    { id: 'creditor_error', label: 'The creditor failed to apply my payment correctly' },
    { id: 'goodwill', label: 'This was a one-time occurrence due to hardship — requesting goodwill removal' }
  ],
  "Collection": [
    { id: 'not_mine_collection', label: 'I do not owe this debt — this account does not belong to me' },
    { id: 'paid_collection', label: 'This debt has been paid in full — please update or remove' },
    { id: 'settled_collection', label: 'This debt was settled — the creditor agreed to update reporting' },
    { id: 'no_validation', label: 'I requested debt validation and it was never provided within 30 days' },
    { id: 'statute_expired', label: 'This debt is past the statute of limitations and should not be reported' },
    { id: 'duplicate_collection', label: 'This is a duplicate of another account already on my report' }
  ],
  "Charge-off": [
    { id: 'not_mine_chargeoff', label: 'This charged-off account does not belong to me' },
    { id: 'paid_chargeoff', label: 'This account was paid after charge-off — status should reflect payment' },
    { id: 'incorrect_balance_co', label: 'The reported balance is incorrect' },
    { id: 'outdated_chargeoff', label: 'This charge-off is older than 7 years from the date of first delinquency' }
  ],
  "Incorrect Balance": [
    { id: 'wrong_amount', label: 'The balance shown does not match my records' },
    { id: 'paid_down', label: 'I have made payments that are not reflected in the reported balance' },
    { id: 'fees_incorrect', label: 'Unauthorized fees or interest have been added to this balance' },
    { id: 'paid_in_full_balance', label: 'This account has been paid in full — balance should be $0' }
  ],
  "default": [
    { id: 'not_mine', label: 'This account does not belong to me' },
    { id: 'paid', label: 'This account was paid or settled' },
    { id: 'incorrect_balance', label: 'The balance is incorrect' },
    { id: 'incorrect_date', label: 'The dates are incorrect' },
    { id: 'duplicate', label: 'This is a duplicate entry' },
    { id: 'outdated', label: 'This information is outdated (>7 years)' },
    { id: 'identity_theft', label: 'This resulted from identity theft' },
    { id: 'no_validation', label: 'Debt validation was requested but never provided' }
  ]
};

function getReasonsForItem(issueType) {
  return REASONS_BY_TYPE[issueType] || REASONS_BY_TYPE["default"];
}

const MAX_DISPUTES_PER_ROUND = 5;

export default function DisputeBuilderPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState({});
  const [selectedBureaus, setSelectedBureaus] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savingDraft, setSavingDraft] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const uploadId = location.state?.uploadId;

  useEffect(() => {
    if (location.state?.items) {
      setItems(location.state.items);
    }
  }, [location.state]);

  // Hydrate draft once when upload_id is known (e.g. returning user)
  useEffect(() => {
    if (!uploadId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API_URL}/api/dispute/draft/${uploadId}`,
          { withCredentials: true }
        );
        if (cancelled || !data?.draft) return;
        if (data.draft.selected_items && Object.keys(data.draft.selected_items).length > 0) {
          setSelectedItems(data.draft.selected_items);
        }
        if (Array.isArray(data.draft.bureaus) && data.draft.bureaus.length > 0) {
          setSelectedBureaus(data.draft.bureaus);
        }
      } catch (err) {
        console.warn('[DisputeBuilder] Draft hydrate failed:', err?.message);
      }
    })();
    return () => { cancelled = true; };
  }, [uploadId]);

  // Auto-save draft whenever selections / bureaus change (debounced)
  useEffect(() => {
    if (!uploadId) return;
    const hasWork = Object.keys(selectedItems).length > 0 || selectedBureaus.length > 0;
    if (!hasWork) return;
    const t = setTimeout(async () => {
      try {
        setSavingDraft(true);
        await axios.put(
          `${API_URL}/api/dispute/draft`,
          {
            upload_id: uploadId,
            selected_items: selectedItems,
            bureaus: selectedBureaus,
          },
          { withCredentials: true }
        );
        setLastSavedAt(new Date());
      } catch (err) {
        console.warn('[DisputeBuilder] Draft autosave failed:', err?.message);
      } finally {
        setSavingDraft(false);
      }
    }, 1200); // debounce
    return () => clearTimeout(t);
  }, [selectedItems, selectedBureaus, uploadId]);

  const handleItemToggle = (index) => {
    setSelectedItems(prev => {
      const newSelected = { ...prev };
      if (newSelected[index]) {
        delete newSelected[index];
      } else {
        // Enforce 5-per-round cap up front so users get a clear message
        if (Object.keys(newSelected).length >= MAX_DISPUTES_PER_ROUND) {
          setError(`You can dispute up to ${MAX_DISPUTES_PER_ROUND} items per round. Uncheck one to pick another, and save the rest for your next round.`);
          return prev;
        }
        newSelected[index] = { reason: '', customExplanation: '' };
        setError('');
      }
      return newSelected;
    });
  };

  const handleReasonChange = (index, reason) => {
    setSelectedItems(prev => ({
      ...prev,
      [index]: { ...prev[index], reason }
    }));
  };

  const handleExplanationChange = (index, customExplanation) => {
    setSelectedItems(prev => ({
      ...prev,
      [index]: { ...prev[index], customExplanation }
    }));
  };

  const handleGenerateLetter = async () => {
    setError('');
    
    // Validate
    const selectedCount = Object.keys(selectedItems).length;
    if (selectedCount === 0) {
      setError('Please select at least one item to dispute');
      return;
    }
    if (selectedCount > MAX_DISPUTES_PER_ROUND) {
      setError(`Disputes are capped at ${MAX_DISPUTES_PER_ROUND} items per round — please unselect a few and save them for your next round.`);
      return;
    }

    if (selectedBureaus.length === 0) {
      setError('Please select at least one credit bureau');
      return;
    }

    // Check all items have reasons
    for (const [index, data] of Object.entries(selectedItems)) {
      if (!data.reason) {
        setError(`Please select a reason for item ${parseInt(index) + 1}`);
        return;
      }
      if (data.reason === 'other' && !data.customExplanation) {
        setError(`Please provide an explanation for item ${parseInt(index) + 1}`);
        return;
      }
    }

    // Check profile completeness
    if (!user?.name || !user?.address || !user?.ssn_last4) {
      setError('Please complete your profile before generating a letter');
      return;
    }

    setLoading(true);

    try {
      const disputeItems = Object.entries(selectedItems).map(([index, data]) => ({
        item_id: index,
        reason: data.reason,
        custom_explanation: data.customExplanation || null
      }));

      if (selectedBureaus.length === 1) {
        // Single bureau
        const { data } = await axios.post(
          `${API_URL}/api/dispute/create`,
          { items: disputeItems, bureau: selectedBureaus[0], upload_id: uploadId },
          { withCredentials: true }
        );
        // Draft consumed — clear it
        if (uploadId) {
          axios.delete(`${API_URL}/api/dispute/draft/${uploadId}`, { withCredentials: true }).catch(() => {});
        }
        navigate('/mail-hub', { state: { dispute: data } });
      } else {
        // Batch - all selected bureaus
        const { data } = await axios.post(
          `${API_URL}/api/dispute/create-batch`,
          { items: disputeItems, bureaus: selectedBureaus, upload_id: uploadId },
          { withCredentials: true }
        );
        if (uploadId) {
          axios.delete(`${API_URL}/api/dispute/draft/${uploadId}`, { withCredentials: true }).catch(() => {});
        }
        navigate('/mail-hub', { state: { dispute: data.disputes[0], batchDisputes: data.disputes } });
      }

    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate dispute letter');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const selectedCount = Object.keys(selectedItems).length;

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="dispute-builder-page">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Shield className="w-8 h-8 text-[#B76E79]" strokeWidth={1.5} />
              <span className="ml-2 font-['Playfair_Display'] text-xl font-semibold text-[#36454F]">
                Selah Slate&trade;
              </span>
            </div>
            <Button 
              variant="ghost" 
              onClick={handleLogout}
              className="text-[#36454F]/70 hover:text-[#36454F] hover:bg-[#36454F]/5 font-['Manrope']"
            >
              Sign Out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Link */}
        <Link 
          to="/upload" 
          className="inline-flex items-center text-[#36454F]/65 hover:text-[#36454F] font-['Manrope'] mb-6 transition-colors"
          data-testid="back-to-upload"
        >
          <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
          Back to Upload
        </Link>

        {/* Page Title */}
        <div className="mb-8 animate-fade-in-up flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-['Playfair_Display'] text-3xl font-semibold text-[#36454F]">
              Dispute Builder
            </h1>
            <p className="mt-2 text-[#36454F]/65 font-['Manrope']">
              Select items to dispute and provide reasons for each
            </p>
          </div>
          {uploadId && (
            <div
              className="flex items-center gap-1.5 text-xs text-[#36454F]/55 font-['Manrope'] px-2.5 py-1.5 rounded-sm bg-[#4CAF50]/5 border border-[#4CAF50]/20"
              data-testid="draft-autosave-indicator"
              title="Your selections are saved so you can log out and come back. Please finish within 7 days — unfinished drafts are auto-cleared after one week."
            >
              <span className={`w-1.5 h-1.5 rounded-full ${savingDraft ? 'bg-[#B76E79] animate-pulse' : 'bg-[#4CAF50]'}`} />
              {savingDraft
                ? 'Saving…'
                : lastSavedAt
                  ? `Saved ${lastSavedAt.toLocaleTimeString([], {hour:'numeric', minute:'2-digit'})} · finish within 7 days`
                  : 'Auto-saves as you work · finish within 7 days'}
            </div>
          )}
        </div>

        {error && (
          <div className="mb-6 p-4 bg-[#D9534F]/10 border border-[#D9534F]/30 text-[#D9534F] text-sm font-['Manrope'] flex items-center animate-fade-in-up" data-testid="builder-error">
            <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" strokeWidth={1.5} />
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Items List */}
          <div className="lg:col-span-2 space-y-4">
            <Card className="border-[#36454F]/15 bg-white animate-fade-in-up">
              <CardHeader>
                <CardTitle className="font-['Playfair_Display'] text-xl text-[#36454F] flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-[#B76E79]" strokeWidth={1.5} />
                  Negative Items ({items.length})
                </CardTitle>
                <CardDescription className="font-['Manrope'] text-[#36454F]/65">
                  Check items you want to dispute and select a reason
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {items.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-[#36454F]/20 mx-auto mb-3" strokeWidth={1.5} />
                    <p className="text-[#36454F]/50 font-['Manrope']">
                      No items found. Please upload a credit report first.
                    </p>
                    <Link to="/upload">
                      <Button 
                        className="mt-4 bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] rounded-sm"
                      >
                        Upload Report
                      </Button>
                    </Link>
                  </div>
                ) : (
                  items.map((item, index) => (
                    <div 
                      key={index}
                      className={`p-4 border rounded-sm transition-all duration-300 ${
                        selectedItems[index] 
                          ? 'border-[#B76E79] bg-[#E0BFB8]/10' 
                          : 'border-[#36454F]/10 bg-[#F5F5F5]'
                      }`}
                      data-testid={`dispute-item-${index}`}
                    >
                      <div className="flex items-start space-x-3">
                        <Checkbox
                          id={`item-${index}`}
                          checked={!!selectedItems[index]}
                          onCheckedChange={() => handleItemToggle(index)}
                          className="mt-1 border-[#36454F]/30 data-[state=checked]:bg-[#B76E79] data-[state=checked]:border-[#B76E79]"
                          data-testid={`item-checkbox-${index}`}
                        />
                        <div className="flex-1">
                          <Label 
                            htmlFor={`item-${index}`}
                            className="font-['Manrope'] font-medium text-[#36454F] cursor-pointer"
                          >
                            {item.account_name}
                          </Label>
                          <div className="mt-1 flex flex-wrap gap-2 text-xs text-[#36454F]/50 font-['Manrope']">
                            <span>{item.account_number}</span>
                            <span>•</span>
                            <span>{item.bureau}</span>
                            {item.balance && (
                              <>
                                <span>•</span>
                                <span>{item.balance}</span>
                              </>
                            )}
                          </div>
                          <span className="inline-block mt-2 text-xs px-2 py-1 bg-[#D9534F]/10 text-[#D9534F] rounded font-['Manrope']">
                            {item.issue_type}
                          </span>

                          {selectedItems[index] && (
                            <div className="mt-4 space-y-3 animate-fade-in-up">
                              <div>
                                <Label className="text-sm font-['Manrope'] text-[#36454F]/80">
                                  Reason for Dispute
                                </Label>
                                <p className="text-xs text-[#B76E79] font-['Manrope'] mt-0.5 mb-1">
                                  Factual reasons for: {item.issue_type}
                                </p>
                                <Select
                                  value={selectedItems[index]?.reason || ''}
                                  onValueChange={(value) => handleReasonChange(index, value)}
                                >
                                  <SelectTrigger 
                                    className="mt-1 border-[#36454F]/15 focus:border-[#B76E79] focus:ring-[#B76E79] rounded-sm font-['Manrope']"
                                    data-testid={`reason-select-${index}`}
                                  >
                                    <SelectValue placeholder="Select a factual reason" />
                                  </SelectTrigger>
                                  <SelectContent className="max-w-[500px] z-[9999] bg-white" position="popper" sideOffset={4}>
                                    {getReasonsForItem(item.issue_type).map((reason) => (
                                      <SelectItem 
                                        key={reason.id} 
                                        value={reason.id}
                                        className="font-['Manrope'] text-sm whitespace-normal py-3 leading-snug cursor-pointer"
                                      >
                                        {reason.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>

                              {selectedItems[index]?.reason === 'other' && (
                                <div>
                                  <Label className="text-sm font-['Manrope'] text-[#36454F]/80">
                                    Explanation
                                  </Label>
                                  <Textarea
                                    value={selectedItems[index]?.customExplanation || ''}
                                    onChange={(e) => handleExplanationChange(index, e.target.value)}
                                    placeholder="Please explain why this item is incorrect..."
                                    className="mt-1 border-[#36454F]/15 focus:border-[#B76E79] focus:ring-[#B76E79] rounded-sm font-['Manrope'] min-h-[80px]"
                                    data-testid={`explanation-input-${index}`}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Bureau Selection & Generate */}
          <div className="space-y-4">
            <Card className="border-[#36454F]/15 bg-white animate-fade-in-up animation-delay-100 sticky top-24">
              <CardHeader>
                <CardTitle className="font-['Playfair_Display'] text-lg text-[#36454F]">
                  Generate Letter
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label className="text-sm font-['Manrope'] text-[#36454F]/80 mb-2 block">
                    Select one or more bureaus <span className="text-[#D9534F]">*</span>
                  </Label>
                  <div className="space-y-2">
                    {BUREAUS.map((bureau) => (
                      <label key={bureau.id} className="flex items-center space-x-2 p-2 rounded-sm hover:bg-[#F5F5F5] cursor-pointer transition-colors">
                        <Checkbox
                          checked={selectedBureaus.includes(bureau.id)}
                          onCheckedChange={(checked) => {
                            setSelectedBureaus(prev =>
                              checked ? [...prev, bureau.id] : prev.filter(b => b !== bureau.id)
                            );
                          }}
                          className="border-[#36454F]/30 data-[state=checked]:bg-[#B76E79] data-[state=checked]:border-[#B76E79]"
                          data-testid={`bureau-check-${bureau.id}`}
                        />
                        <span className="font-['Manrope'] text-sm text-[#36454F]">{bureau.label}</span>
                      </label>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-[#36454F]/50 font-['Manrope']">
                    {selectedBureaus.length > 1
                      ? `${selectedBureaus.length} letters will be generated`
                      : selectedBureaus.length === 1
                        ? '1 letter will be generated'
                        : 'Pick at least one bureau to dispute with'}
                  </p>
                </div>

                <div className="p-4 bg-[#F5F5F5] rounded-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-['Manrope'] text-[#36454F]/65">Items selected</span>
                    <span className="font-['Manrope'] font-semibold text-[#36454F]">{selectedCount}</span>
                  </div>
                  {selectedCount > 0 && selectedBureaus.length > 0 && (
                    <div className="flex items-center text-xs text-[#4CAF50] font-['Manrope']">
                      <CheckCircle className="w-3 h-3 mr-1" strokeWidth={1.5} />
                      Ready to generate {selectedBureaus.length > 1 ? `${selectedBureaus.length} letters` : ''}
                    </div>
                  )}
                </div>

                <Button
                  onClick={handleGenerateLetter}
                  disabled={loading || selectedCount === 0 || selectedBureaus.length === 0}
                  className="w-full h-12 bg-[#B76E79] hover:bg-[#9e5c66] text-white font-['Manrope'] font-medium rounded-sm transition-all duration-300 disabled:opacity-50"
                  data-testid="generate-letter-btn"
                >
                  {loading ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Generating...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      {selectedBureaus.length > 1 ? `Generate ${selectedBureaus.length} Letters` : 'Generate Letter'}
                      <ArrowRight className="w-5 h-5 ml-2" strokeWidth={1.5} />
                    </span>
                  )}
                </Button>

                {(!user?.name || !user?.address || !user?.ssn_last4) && (
                  <div className="p-3 bg-[#E0BFB8]/20 border border-[#B76E79]/30 rounded-sm">
                    <p className="text-xs text-[#36454F]/80 font-['Manrope']">
                      <AlertCircle className="w-3 h-3 inline mr-1" strokeWidth={1.5} />
                      Complete your <Link to="/profile" className="text-[#B76E79] underline">profile</Link> to generate letters
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      <AppFooter />
    </div>
  );
}
